import { Link, useSearchParams } from 'react-router-dom';
import Pagination from '@/components/feature/Pagination';
import { newsCategories, newsList } from '@/mocks/news';

const PAGE_SIZE = 6;

export default function NewsBrowser() {
  const [params, setParams] = useSearchParams();
  const active = params.get('category') || '全部';
  const requestedPage = Math.max(1, Number(params.get('page') || '1') || 1);

  const filtered = active === '全部' ? newsList : newsList.filter((item) => item.category === active);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const items = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const switchCategory = (category: string) => {
    const next = new URLSearchParams();
    if (category !== '全部') next.set('category', category);
    setParams(next, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
      <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-accent-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">影视资讯</span>
        </nav>
        <h1 className="mt-3 font-display text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
          新片上线消息、深度影评解读、行业观察与平台活动公告。我们把每天散落在各处的影视信息，整理成可以慢慢读的条目。
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-1 rounded-full border border-background-300/60 bg-background-100 p-1">
          {newsCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => switchCategory(category)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                active === category
                  ? 'bg-primary-950 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              {category}
            </button>
          ))}
          <span className="ml-auto hidden pr-3 font-label text-xs text-foreground-400 sm:block">
            共 {filtered.length} 篇
          </span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-article-line text-xl"></i>
          </span>
          <p className="mt-4 text-base font-semibold text-foreground-950">该分类暂无资讯</p>
          <p className="mt-1.5 text-sm text-foreground-500">试试切换到其他分类，或查看全部资讯。</p>
          <button
            type="button"
            onClick={() => switchCategory('全部')}
            className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
          >
            查看全部资讯
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-accent-300"
            >
              <div className="relative h-[180px] w-full overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 影视资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-foreground-950">
                  {item.category}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h2 className="line-clamp-2 font-display text-base font-bold leading-snug text-foreground-950 group-hover:text-accent-700">
                  {item.title}
                </h2>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">{item.summary}</p>
                <div className="mt-auto flex items-center justify-between pt-3 text-[11px] text-foreground-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line"></i>
                    {item.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.date}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <Pagination current={currentPage} total={totalPages} />

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3.5">
        <p className="text-xs leading-relaxed text-accent-900/80">
          <i className="ri-mail-line mr-1"></i>
          有想推荐的作品或新闻线索？通过「关于我们」页面的联系邮箱告诉我们。
        </p>
        <Link
          to="/about"
          className="whitespace-nowrap rounded-md bg-primary-950 px-3.5 py-1.5 text-xs text-background-50 transition-colors hover:bg-primary-800"
        >
          联系我们
        </Link>
      </div>
    </div>
  );
}